/**
 * useSeoTags — central per-page SEO/Open-Graph/Twitter/JSON-LD hook.
 *
 * Track 1A of `doc/migration/seo-and-nextjs-plan.md`. Lives in `hooks/` (not
 * `components/`) because the call shape is hook-like — every page invokes it
 * once at the top of its render. The hook returns JSX containing a single
 * `<Helmet>` block; the caller renders it inline.
 *
 * Pattern at call site:
 *   const seoTags = useSeoTags({ title, description, canonical, jsonLd });
 *   return (
 *     <>
 *       {seoTags}
 *       ...page content...
 *     </>
 *   );
 *
 * Defaults applied here so individual pages stay terse:
 *   - title         appended with " — Loopy" suffix unless it already contains "Loopy"
 *   - ogType        'website'
 *   - ogImage       absolute URL to /icons/loopy_512.png
 *   - canonical     `${SITE_ORIGIN}${current pathname}` if omitted
 *   - hreflangs     emitted for every supported locale + `x-default`. Loopy
 *                   does not have locale-prefixed URLs today, so each alt URL
 *                   reuses the canonical with a `?lang=<code>` query — the
 *                   existing language-switcher mechanism. Phase 2 (Next.js
 *                   migration) replaces this with proper `/[locale]/` paths.
 *
 * Note: react-helmet-async manipulates `document.head` after hydration, so
 * crawlers without JS execution (GPTBot, ClaudeBot, etc.) WILL NOT see these
 * tags until Phase 1D (react-snap prerender) lands. Phase 1A still buys us
 * Google JS-pass indexing + LLM crawlers that DO run JS (Perplexity,
 * ChatGPT-User), so it's worth shipping now.
 */

import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export const SITE_ORIGIN = 'https://loopyai.app';
export const BRAND_NAME = 'Loopy';
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/icons/loopy_512.png`;
const TWITTER_HANDLE = '@loopyai';
const SUPPORTED_LOCALES = ['en', 'ja', 'ko', 'zh-CN', 'zh-TW', 'vi', 'th', 'id', 'ms'] as const;

export type SeoOgType =
  | 'website'
  | 'article'
  | 'profile'
  | 'video.other'
  | 'product'
  | 'book';

export interface UseSeoTagsOptions {
  /** Page title. Brand suffix " — Loopy" is appended automatically. */
  title: string;
  /** Meta description. ~155 chars recommended for SERP truncation. */
  description: string;
  /** Override canonical URL. Defaults to current pathname under SITE_ORIGIN. */
  canonical?: string;
  /** Absolute or root-relative OG image URL. Default: /icons/loopy_512.png. */
  ogImage?: string;
  /** Open Graph type. Default 'website'. */
  ogType?: SeoOgType;
  /** Optional schema.org JSON-LD payload (object or array of objects). */
  jsonLd?: object | object[];
  /**
   * Optional hreflang alternates `{ locale: url }`. If omitted, we emit a
   * stock set covering all 9 supported locales pointing at
   * `${canonical}?lang=<code>` + `x-default` → canonical. Pages with truly
   * locale-specific URLs (none today) can pass overrides.
   */
  hreflangs?: Record<string, string>;
  /** Set to true on pages that should NOT be indexed (e.g. preview routes). */
  noindex?: boolean;
}

/** Build an absolute URL on the site origin. */
function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_ORIGIN}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

/** Ensure the brand suffix is present without doubling it up. */
function applyBrandSuffix(title: string): string {
  const trimmed = title.trim();
  if (!trimmed) return BRAND_NAME;
  // already mentions Loopy in any case form? leave alone.
  if (/loopy/i.test(trimmed)) return trimmed;
  return `${trimmed} — ${BRAND_NAME}`;
}

export function useSeoTags(options: UseSeoTagsOptions): React.ReactElement {
  const { i18n } = useTranslation();
  const location = useLocation();

  const finalTitle = applyBrandSuffix(options.title);
  const finalCanonical = options.canonical
    ? absoluteUrl(options.canonical)
    : absoluteUrl(location.pathname || '/');
  const finalOgImage = absoluteUrl(options.ogImage || DEFAULT_OG_IMAGE);
  const finalOgType = options.ogType || 'website';
  const currentLocale = i18n.language || 'en';

  // Compute hreflang alternates. Stock pattern: same canonical with ?lang= query.
  // x-default points at the bare canonical.
  const hreflangs = useMemo(() => {
    if (options.hreflangs) return options.hreflangs;
    const base = finalCanonical.split('?')[0]; // strip any pre-existing query
    const map: Record<string, string> = {};
    for (const locale of SUPPORTED_LOCALES) {
      // Use ?lang= as the query parameter — matches how the language switcher
      // hands off between locales today. When Phase 2 introduces /[locale]/
      // route prefixes we can swap this implementation without touching pages.
      map[locale] = `${base}?lang=${locale}`;
    }
    map['x-default'] = base;
    return map;
  }, [options.hreflangs, finalCanonical]);

  const jsonLdArray = options.jsonLd
    ? Array.isArray(options.jsonLd)
      ? options.jsonLd
      : [options.jsonLd]
    : [];

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={options.description} />
      {options.noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={finalCanonical} />

      {/* Open Graph */}
      <meta property="og:site_name" content={BRAND_NAME} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={options.description} />
      <meta property="og:type" content={finalOgType} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:locale" content={currentLocale.replace('-', '_')} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={options.description} />
      <meta name="twitter:image" content={finalOgImage} />

      {/* hreflang alternates — see comment in hook body about ?lang= scheme */}
      {Object.entries(hreflangs).map(([locale, url]) => (
        <link
          key={locale}
          rel="alternate"
          hrefLang={locale}
          href={url}
        />
      ))}

      {/* JSON-LD structured data */}
      {jsonLdArray.map((blob, idx) => (
        <script
          key={idx}
          type="application/ld+json"
          // react-helmet-async serializes the child text node; the JSON.stringify
          // result is plain text, which Helmet inserts verbatim into the script.
        >
          {JSON.stringify(blob)}
        </script>
      ))}
    </Helmet>
  );
}

// ---------- JSON-LD factory helpers ----------
// Co-located here so pages can do:
//   import { useSeoTags, jsonLdOrganization } from '../hooks/useSeoTags';
// without leaking schema.org boilerplate into every page.

export const ORGANIZATION_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Loopy',
  alternateName: 'Loopy AI',
  url: SITE_ORIGIN,
  logo: DEFAULT_OG_IMAGE,
  sameAs: [
    'https://twitter.com/loopyai',
    'https://www.instagram.com/loopyai.app',
  ],
};

export const WEBSITE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Loopy',
  url: SITE_ORIGIN,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_ORIGIN}/courses?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

/** Generic WebPage JSON-LD with breadcrumbs. */
export function buildWebPageJsonLd(args: {
  url: string;
  name: string;
  description: string;
  breadcrumbs: Array<{ name: string; url: string }>;
}): object[] {
  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: args.breadcrumbs.map((crumb, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.url),
    })),
  };
  const webPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: absoluteUrl(args.url),
    name: args.name,
    description: args.description,
  };
  return [webPage, breadcrumbList];
}

/** ItemList JSON-LD — used by /courses to expose first-page course list. */
export function buildCourseItemListJsonLd(args: {
  listName: string;
  items: Array<{ id: string | number; title: string; url?: string }>;
}): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: args.listName,
    itemListElement: args.items.slice(0, 10).map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: item.title,
      url: absoluteUrl(item.url || `/courses/${item.id}`),
    })),
  };
}

/** FAQ JSON-LD — accepts pre-flattened Q&A pairs. */
export function buildFaqJsonLd(items: Array<{ q: string; a: string }>): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };
}

/**
 * Course JSON-LD — emits schema.org/Course for a single course detail page.
 *
 * Loopy courses are English-learning content (regardless of UI locale), so
 * `inLanguage` is hardcoded to "en". CEFR level is mapped onto
 * `educationalLevel`. Free-vs-paid is `isAccessibleForFree`; we treat any
 * course without an explicit price as free since Loopy's model is
 * subscription-gated (the course itself doesn't carry a per-unit price).
 *
 * Undefined fields are stripped so we don't emit `"image": undefined` etc.
 */
export interface CourseJsonLdInput {
  id: string | number;
  title: string;
  description?: string;
  /** Absolute image URL. Caller is responsible for resolving (e.g. resolveMediaUrl). */
  image?: string;
  cefrLevel?: string;
  publishedAt?: string;
  updatedAt?: string;
  /** Free course flag (true if explicitly free or no paid price set). */
  isFree?: boolean;
}

export function buildCourseJsonLd(course: CourseJsonLdInput): object {
  const payload: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: course.title,
    description: course.description,
    url: `${SITE_ORIGIN}/courses/${course.id}`,
    image: course.image ? absoluteUrl(course.image) : undefined,
    inLanguage: 'en', // Loopy courses are English learning content
    educationalLevel: course.cefrLevel,
    datePublished: course.publishedAt,
    dateModified: course.updatedAt,
    provider: {
      '@type': 'Organization',
      name: 'Loopy',
      url: SITE_ORIGIN,
    },
    audience: {
      '@type': 'EducationalAudience',
      educationalRole: 'student',
    },
    isAccessibleForFree: course.isFree ?? true,
  };
  // Strip undefined values so JSON.stringify omits them entirely.
  for (const key of Object.keys(payload)) {
    if (payload[key] === undefined) delete payload[key];
  }
  return payload;
}
